import { Panel, PanelConfig, Door, Drain, DrawingInfo } from '../types';
import { PolyPanel, Pt } from '../engine/polyEngine';
import { drawPolyPanels, drawOutline } from '../engine/polyRender';

interface PrintOptions {
  horizontal: number;
  vertical: number;
  panelThick: number;
  walls: { left: Panel[]; right: Panel[]; top: Panel[]; bottom: Panel[] };
  doors: Door[];
  drains: Drain[];
  config: PanelConfig;
  drawingInfo: DrawingInfo;
  poly?: { panels: PolyPanel[]; outline: Pt[] } | null; // 있으면 화면과 같은 다각형 엔진 결과로 인쇄
  scene?: ((w: number, h: number) => HTMLCanvasElement) | null; // 있으면 화면 그림(치수·천장·문·칸막이 포함) 그대로 인쇄
}

/**
 * 도면을 가로 방향 인쇄용 캔버스에 그리고 인쇄 실행
 */
export function printDrawing(opts: PrintOptions) {
  const { horizontal, vertical, panelThick, walls, doors, drains, config, drawingInfo, poly } = opts;

  // A4 가로 (landscape) 비율: 297mm x 210mm
  const pageW = 1120;
  const pageH = 790;
  const margin = 20;
  const infoBoxW = 200;
  const drawAreaW = pageW - margin * 2 - infoBoxW - 10;
  const drawAreaH = pageH - margin * 2;
  const padding = 60;

  // 스케일 계산
  const scaleX = (drawAreaW - padding * 2) / horizontal;
  const scaleY = (drawAreaH - padding * 2) / vertical;
  const scale = Math.min(scaleX, scaleY);

  // 오프셋 (도면 중앙 정렬)
  const ox = margin + padding + (drawAreaW - padding * 2 - horizontal * scale) / 2;
  const oy = margin + padding + (drawAreaH - padding * 2 - vertical * scale) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = pageW * 3;
  canvas.height = pageH * 3;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(3, 3); // 고해상도

  // 배경
  ctx.fillStyle = '#FFF';
  ctx.fillRect(0, 0, pageW, pageH);

  // 외곽 테두리
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(margin, margin, pageW - margin * 2, pageH - margin * 2);

  // === 도면 영역 ===
  const sceneCanvas = opts.scene?.(drawAreaW, drawAreaH);
  if (sceneCanvas) {
    // 화면과 같은 그림
    ctx.drawImage(sceneCanvas, margin + 1, margin + 1, drawAreaW - 2, drawAreaH - 2);
  } else {
  // 벽 외곽선
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 2;
  if (poly) drawOutline(ctx, poly.outline, { scale, ox, oy });
  else ctx.strokeRect(ox, oy, horizontal * scale, vertical * scale);

  // 패널 그리기 함수
  const drawPanel = (x: number, y: number, w: number, h: number, size: number, isCustom: boolean, grooveDir?: string, isVert?: boolean) => {
    const sx = ox + x * scale, sy = oy + y * scale;
    const sw = w * scale, sh = h * scale;

    ctx.fillStyle = isCustom ? '#FFE0B2' : '#E8F0FE';
    ctx.fillRect(sx, sy, sw, sh);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 0.8;
    ctx.strokeRect(sx, sy, sw, sh);

    ctx.fillStyle = '#333';
    ctx.font = '9px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (Math.min(sw, sh) > 10) {
      ctx.fillText(`${size}`, sx + sw / 2, sy + sh / 2);
    }

    // 그루브 방향
    if (grooveDir && grooveDir !== 'NONE' && Math.min(sw, sh) > 8) {
      ctx.fillStyle = '#F57C00';
      const ts = Math.min(4, Math.min(sw, sh) * 0.12);
      ctx.beginPath();
      if (isVert) {
        const cx = sx + sw / 2;
        if (grooveDir === 'LEFT') {
          ctx.moveTo(cx, sy + 2); ctx.lineTo(cx - ts, sy + 2 + ts); ctx.lineTo(cx + ts, sy + 2 + ts);
        } else {
          ctx.moveTo(cx, sy + sh - 2); ctx.lineTo(cx - ts, sy + sh - 2 - ts); ctx.lineTo(cx + ts, sy + sh - 2 - ts);
        }
      } else {
        const cy = sy + sh / 2;
        if (grooveDir === 'LEFT') {
          ctx.moveTo(sx + 2, cy); ctx.lineTo(sx + 2 + ts, cy - ts); ctx.lineTo(sx + 2 + ts, cy + ts);
        } else {
          ctx.moveTo(sx + sw - 2, cy); ctx.lineTo(sx + sw - 2 - ts, cy - ts); ctx.lineTo(sx + sw - 2 - ts, cy + ts);
        }
      }
      ctx.closePath();
      ctx.fill();
    }
  };

  let posX = 0;
  let posY = 0;
  if (poly) {
    // 화면과 같은 다각형 엔진 결과 (벽·하리 패널, 결합 삼각형, 라벨)
    drawPolyPanels(ctx, poly.panels, panelThick, { scale, ox, oy });
  } else {
    // 상단 벽
    walls.top.forEach(p => { drawPanel(posX, 0, p.size, panelThick, p.size, p.isCustom, p.grooveDir, false); posX += p.size + config.PANEL_SPACE; });
    // 하단 벽
    posX = 0;
    walls.bottom.forEach(p => { drawPanel(posX, vertical - panelThick, p.size, panelThick, p.size, p.isCustom, p.grooveDir, false); posX += p.size + config.PANEL_SPACE; });
    // 좌측 벽
    walls.left.forEach(p => { drawPanel(0, posY, panelThick, p.size, p.size, p.isCustom, p.grooveDir, true); posY += p.size + config.PANEL_SPACE; });
    // 우측 벽
    posY = 0;
    walls.right.forEach(p => { drawPanel(horizontal - panelThick, posY, panelThick, p.size, p.size, p.isCustom, p.grooveDir, true); posY += p.size + config.PANEL_SPACE; });
  }

  // 문
  doors.forEach(door => {
    const isHoriz = door.wallSide === 'top' || door.wallSide === 'bottom';
    if (isHoriz) {
      const dy = door.wallSide === 'top' ? 0 : vertical - panelThick;
      const doorX = door.position + door.leftPanel + config.PANEL_SPACE;
      const sx = ox + doorX * scale, sy = oy + dy * scale;
      const sw = door.width * scale, sh = panelThick * scale;
      ctx.fillStyle = '#C8E6C9';
      ctx.fillRect(sx, sy, sw, sh);
      ctx.strokeStyle = '#2E7D32'; ctx.lineWidth = 1.5;
      ctx.strokeRect(sx, sy, sw, sh);
      ctx.fillStyle = '#2E7D32'; ctx.font = '8px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(`문 ${door.width}`, sx + sw / 2, sy + sh / 2);
    } else {
      const dx = door.wallSide === 'left' ? 0 : horizontal - panelThick;
      const doorY = door.position + door.leftPanel + config.PANEL_SPACE;
      const sx = ox + dx * scale, sy = oy + doorY * scale;
      const sw = panelThick * scale, sh = door.width * scale;
      ctx.fillStyle = '#C8E6C9';
      ctx.fillRect(sx, sy, sw, sh);
      ctx.strokeStyle = '#2E7D32'; ctx.lineWidth = 1.5;
      ctx.strokeRect(sx, sy, sw, sh);
    }
  });

  // 배수구
  drains.forEach(drain => {
    const cx = ox + drain.x * scale, cy = oy + drain.y * scale;
    const r = (drain.size / 2) * scale;
    ctx.strokeStyle = '#1565C0'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.6, 0, Math.PI * 2); ctx.stroke();
    ctx.lineWidth = 0.8;
    ctx.beginPath(); ctx.moveTo(cx - r, cy); ctx.lineTo(cx + r, cy); ctx.moveTo(cx, cy - r); ctx.lineTo(cx, cy + r); ctx.stroke();
  });

  // 치수선 (상단) — 다각형이면 상단 외벽 띠 패널 위치 그대로
  const topDims: [number, number, number][] = poly
    ? poly.panels.filter(p => p.a.y === p.b.y && p.a.y < panelThick).map(p => [Math.min(p.a.x, p.b.x), p.size, 0] as [number, number, number])
    : (() => { let x = 0; return walls.top.map(p => { const r: [number, number, number] = [x, p.size, 0]; x += p.size + config.PANEL_SPACE; return r; }); })();
  topDims.forEach(([x0, size]) => {
    const p = { size };
    posX = x0;
    const sx1 = ox + posX * scale, sx2 = ox + (posX + p.size) * scale;
    const dy = oy - 20;
    ctx.strokeStyle = '#666'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(sx1, oy - 3); ctx.lineTo(sx1, dy - 3); ctx.moveTo(sx2, oy - 3); ctx.lineTo(sx2, dy - 3); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(sx1, dy); ctx.lineTo(sx2, dy); ctx.stroke();
    ctx.fillStyle = '#333'; ctx.font = '8px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(`${p.size}`, (sx1 + sx2) / 2, dy - 7);
  });
  // 전체 가로 치수
  const totalDy = oy - 38;
  ctx.beginPath(); ctx.moveTo(ox, totalDy); ctx.lineTo(ox + horizontal * scale, totalDy); ctx.stroke();
  ctx.fillText(`${horizontal}`, ox + horizontal * scale / 2, totalDy - 7);

  // 치수선 (좌측)
  const leftDims: [number, number][] = poly
    ? poly.panels.filter(p => p.a.x === p.b.x && p.a.x < panelThick).map(p => [Math.min(p.a.y, p.b.y), p.size] as [number, number])
    : (() => { let y = 0; return walls.left.map(p => { const r: [number, number] = [y, p.size]; y += p.size + config.PANEL_SPACE; return r; }); })();
  leftDims.forEach(([y0, size]) => {
    const p = { size };
    posY = y0;
    const sy1 = oy + posY * scale, sy2 = oy + (posY + p.size) * scale;
    const dx = ox - 20;
    ctx.beginPath(); ctx.moveTo(ox - 3, sy1); ctx.lineTo(dx - 3, sy1); ctx.moveTo(ox - 3, sy2); ctx.lineTo(dx - 3, sy2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(dx, sy1); ctx.lineTo(dx, sy2); ctx.stroke();
    ctx.save(); ctx.translate(dx - 7, (sy1 + sy2) / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillText(`${p.size}`, 0, 0); ctx.restore();
  });
  // 전체 세로 치수
  const totalDx = ox - 38;
  ctx.beginPath(); ctx.moveTo(totalDx, oy); ctx.lineTo(totalDx, oy + vertical * scale); ctx.stroke();
  ctx.save(); ctx.translate(totalDx - 7, oy + vertical * scale / 2); ctx.rotate(-Math.PI / 2);
  ctx.fillText(`${vertical}`, 0, 0); ctx.restore();

  }

  // === 도면 정보란 (우측) ===
  const infoX = pageW - margin - infoBoxW;
  const infoY = margin;
  const rowH = 22;
  ctx.strokeStyle = '#000'; ctx.lineWidth = 1;
  ctx.strokeRect(infoX, infoY, infoBoxW, rowH * 10);

  const infoRows = [
    ['품명', drawingInfo.panelType],
    ['규격', drawingInfo.panelSpec || `${horizontal}x${vertical}x${panelThick}`],
    ['그루브', drawingInfo.grooveInfo],
    ['현장명', drawingInfo.siteName],
    ['도면번호', drawingInfo.drawingNo],
    ['날짜', drawingInfo.date],
    ['작성자', drawingInfo.drawnBy],
    ['비고', drawingInfo.memo],
  ];

  // 타이틀
  ctx.fillStyle = '#1976D2';
  ctx.fillRect(infoX, infoY, infoBoxW, rowH);
  ctx.fillStyle = '#FFF'; ctx.font = 'bold 10px Arial'; ctx.textAlign = 'center';
  ctx.fillText('도면 정보', infoX + infoBoxW / 2, infoY + rowH / 2);

  // 회사명
  ctx.fillStyle = '#f0f0f0';
  ctx.fillRect(infoX, infoY + rowH, infoBoxW, rowH);
  ctx.strokeRect(infoX, infoY + rowH, infoBoxW, rowH);
  ctx.fillStyle = '#333'; ctx.font = 'bold 11px Arial';
  ctx.fillText('Panel Draw', infoX + infoBoxW / 2, infoY + rowH + rowH / 2);

  infoRows.forEach((row, i) => {
    const ry = infoY + rowH * (i + 2);
    ctx.strokeStyle = '#ccc'; ctx.lineWidth = 0.5;
    ctx.strokeRect(infoX, ry, infoBoxW, rowH);
    // 레이블
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(infoX, ry, 60, rowH);
    ctx.strokeRect(infoX, ry, 60, rowH);
    ctx.fillStyle = '#555'; ctx.font = '8px Arial'; ctx.textAlign = 'center';
    ctx.fillText(row[0], infoX + 30, ry + rowH / 2);
    // 값
    ctx.fillStyle = '#333'; ctx.font = '9px Arial'; ctx.textAlign = 'left';
    ctx.fillText(row[1], infoX + 65, ry + rowH / 2);
  });

  // === 인쇄 실행 ===
  const dataUrl = canvas.toDataURL('image/png');
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>도면 인쇄 - ${drawingInfo.siteName || 'Panel Draw'}</title>
        <style>
          @page { size: landscape; margin: 5mm; }
          body { margin: 0; display: flex; justify-content: center; align-items: center; }
          img { width: 100%; max-height: 100vh; }
          @media print { body { margin: 0; } img { width: 100%; } }
        </style>
      </head>
      <body>
        <img src="${dataUrl}" onload="window.print(); window.close();" />
      </body>
      </html>
    `);
    printWindow.document.close();
  }
}
